import {Inject, Injectable, Optional} from "@angular/core";
import {BehaviorSubject, of, Subject} from "rxjs";
import {catchError, finalize, switchMap, take, takeUntil, tap} from "rxjs/operators";
import {ActivatedRoute, Router} from "@angular/router";
import {IAbstractListConfigParams, IIntAbstractListService} from "./types";
import {DestroyStreamService} from "../destroy-stream.service";
import {INT_ABSTRACT_LIST_API_REQUEST} from "./abstarct-list.token";

@Injectable()
export class AbstractListService<P extends {}, R> {

  reload$ = new BehaviorSubject(false);
  response$ = new BehaviorSubject<R>({} as R);
  paginator$ = new BehaviorSubject<any>({});
  load$ = new BehaviorSubject<boolean>(false);
  readonly firstLoadParamsStream$ = new BehaviorSubject({} as P);

  params = {} as P;
  isFirstLoadHasBeen = false;

  paramsHasBeenUpdated$ = new Subject();

  constructor(
    @Optional()
    @Inject(INT_ABSTRACT_LIST_API_REQUEST)
    private readonly api: IIntAbstractListService<P, R>,
    private readonly ar: ActivatedRoute,
    private readonly router: Router,
    @Optional()
    private readonly destroy$: DestroyStreamService
  ) {}

  init(params: P = {} as P): void {
    if(!this.destroy$) {
      console.log(`Please provide DestroyStreamService for AbstractListService`)
    }
    if (!this.api) {
      console.warn("Set api service(use 'INT_ABSTRACT_LIST_API_REQUEST' provider)");
      return undefined;
    }
    this.reload$.pipe(switchMap(() => {
      return this.ar.queryParams
        .pipe(
          tap(() => {
            this.load$.next(true);
          }),
          switchMap((p: any) => {
            this.params = this.decrementPageIndexForRequest(this.mergeParams(params, p));
            if(!this.isFirstLoadHasBeen) {
              this.firstLoadParamsStream$.next(this.params);
            }

            const _params = this.params;

            this.isFirstLoadHasBeen = true;

            return this.api.request(_params).pipe(
              catchError(() => {
                return of([])
              }),
              take(1),
              finalize(() => {
                this.load$.next(false);
              }),
              takeUntil(this.destroy$),
            );
          }),
          takeUntil(this.destroy$)
        )
    })).subscribe((r: any) => {
        this.paginator$.next({ ...this.params });
        this.response$.next(r);
      });
  }

  reload() {
    this.reload$.next(true);
  }

  decrementPageIndexForRequest(o: P): P {
    if ('page' in o) {
      // @ts-ignore
      return {...o, page: o?.page > 0 ? o?.page - 1 : 0}
    }
    return o;
  }


  private mergeParams(base: P, res: P): P {
    return { ...base, ...res };
  }

  ifDeletedParamsValid(params: unknown): boolean {
    return Boolean(params)
      && Array.isArray(params)
      && params.length > 0
  }

  setParams(params: P, config?: IAbstractListConfigParams) {

    if(config?.clearParams) {
      this.params = {} as P;
    }

    let payloadParams: P = this.mergeParams(this.params, params);

    if(this.ifDeletedParamsValid(config?.deleteParams)) {
      const deleted = config?.deleteParams || [];
      const output = Object.entries(payloadParams).filter(([key, _]) => {
        return deleted.indexOf(key) === -1;
      }).reduce<P>((acc, [key, value]) => {
        // @ts-ignore
        acc[key] = value;
        return acc;
      }, {} as P);
      this.params = output;
    }else{
      this.params = payloadParams;
    }

    this.paramsHasBeenUpdated$.next(this.params);

    this.router.navigate([], {
      relativeTo: this.ar,
      queryParams: this.params
    });
  }
}
